import React from 'react';
import { Box, Typography } from '@mui/material';
import DOMPurify from 'dompurify';

// Open any provider links in a new tab and harden against reverse-tabnabbing.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'b', 'i', 'em', 'strong', 'u', 's', 'del', 'ins',
    'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'blockquote', 'pre', 'code', 'a',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'div', 'span', 'hr',
  ],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
  ADD_ATTR: ['target'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'button', 'object', 'embed', 'img'],
  RETURN_TRUSTED_TYPE: false as const,
};

const htmlSx = {
  fontSize: '0.875rem',
  lineHeight: 1.6,
  wordBreak: 'break-word' as const,
  '& p': { my: 0.75 },
  '& p:first-of-type': { mt: 0 },
  '& p:last-of-type': { mb: 0 },
  '& ul, & ol': { my: 0.75, pl: 3 },
  '& li': { mb: 0.25 },
  '& a': { color: 'primary.main' },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.85em',
    bgcolor: 'action.hover',
    px: 0.5,
    borderRadius: 0.5,
  },
  '& pre': {
    bgcolor: 'action.hover',
    p: 1,
    borderRadius: 1,
    overflow: 'auto',
    whiteSpace: 'pre-wrap' as const,
  },
  '& blockquote': {
    borderLeft: 2,
    borderColor: 'divider',
    pl: 1.5,
    my: 0.75,
    ml: 0,
    color: 'text.secondary',
  },
  '& table': { borderCollapse: 'collapse' as const, my: 0.75 },
  '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5 },
  '& h1, & h2, & h3, & h4, & h5, & h6': { fontSize: '1rem', fontWeight: 600, mt: 1, mb: 0.5 },
};

interface TicketContentProps {
  /** Provider HTML (preferred when present). */
  html?: string | null;
  /** Plain-text fallback. */
  text?: string | null;
}

/** Renders ITSM-supplied ticket content: sanitized HTML when the provider gives
 *  it, otherwise the plain-text fallback. Format is decided at the integration
 *  boundary (both representations are carried on the view) — never guessed here. */
const TicketContent: React.FC<TicketContentProps> = ({ html, text }) => {
  if (html && html.trim()) {
    return (
      <Box sx={htmlSx} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, SANITIZE_CONFIG) }} />
    );
  }
  if (text && text.trim()) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
        {text}
      </Typography>
    );
  }
  return null;
};

export default TicketContent;
